import {type ChangeEvent, type JSX, type MouseEvent, type ReactElement, useEffect} from 'react'

import {adminCreateNewUser, adminGetNotAuthorizedUsers, adminGetUsers, adminUpdateUserAccess} from '../../actions'
import {useAppDispatch, useAppSelector, useEmailInput} from '../../hooks'
import type {AdminUser} from '../../reducers/types'
import type {RootState} from '../../store'
import {classNames} from '../../utils'
import {useErrorNotifications} from "../common/Notifications";
import Tooltip from "../common/Tooltip";

/**
 * Admin section's user list component.
 */
export function AdminUsers(): JSX.Element {
  const users = useAppSelector<AdminUser[]>((state: RootState) => state.admin.users)
  const notAuthorizedUsers = useAppSelector<AdminUser[]>((state: RootState) => state.admin.notAuthorizedUsers)

  const usersPending = useAppSelector<boolean>((state: RootState) => state.admin.usersPending)
  const notAuthorizedUsersPending = useAppSelector<boolean>((state: RootState) => state.admin.notAuthorizedUsersPending)

  const dispatch = useAppDispatch()

  useErrorNotifications((state) => state.admin.usersError)
  useErrorNotifications((state) => state.admin.notAuthorizedUsersError)

  useEffect(() => {
    dispatch(adminGetUsers())
    dispatch(adminGetNotAuthorizedUsers())
  }, [dispatch])

  if (usersPending || notAuthorizedUsersPending) {
    return (
      <div className='spinner-border' role='status'>
        <span className='visually-hidden'>Loading...</span>
      </div>
    )
  }

  return (
    <>
      <UserList header="Hyväksyntää odottavat käyttäjät" users={notAuthorizedUsers}/>
      <UserList header="Sallitut käyttäjät" users={users} allowNewUserCreation/>
    </>
  )
}

/* Sub Components */


interface UserListProps {
  header: string,
  users: AdminUser[]
  allowNewUserCreation?: boolean
}

function UserList(props: Readonly<UserListProps>): ReactElement {
  const myEmail = useAppSelector<string | null>((state: RootState) => state.user.claims?.email ?? null)
  const sortedUsers = sortUsers(props.users)
  const allowNewUserCreation = props.allowNewUserCreation ?? false

  return (
    <div className='form-outline pb-5'>
      <h1>{props.header}</h1>
      <table className='table'>
        <thead>
        <tr>
          <th>Email</th>
          <th>Auth0Id</th>
          <th>Suunnittelijan käyttö sallittu</th>
          <th>Järjestelmän pääkäyttäjä</th>
          <th>&nbsp;</th>
        </tr>
        </thead>
        <tbody>
        {sortedUsers.map(u => (
          <AdminUsersRow key={u.email} user={u} disabled={u.email === myEmail}/>
        ))}
        </tbody>
        <tfoot>
        {allowNewUserCreation && <AdminUsersNewUserRow/>}
        </tfoot>
      </table>
    </div>
  )
}

export function AdminUsersNewUserRow(): JSX.Element {
  const [emailInput, email] = useEmailInput('')
  const dispatch = useAppDispatch()
  const isDirty = email !== ''

  async function onSave(e: MouseEvent<HTMLAnchorElement>) {
    e.preventDefault()

    if (!isDirty) {
      return
    }

    await dispatch(adminCreateNewUser({
      email,
      useAuthorized: true,
      isAdmin: false
    }))
  }

  const saveClasses = {
    'btn btn-success btn-new-user-save me-3': true,
    disabled: !isDirty
  }

  return (
    <tr>
      <td>
        {emailInput}
      </td>
      <td>&nbsp;</td>
      <td>
        <div className='form-check form-switch'>
          <input className='form-check-input' type='checkbox' disabled={true} checked readOnly/>
        </div>
      </td>
      <td>
        <div className='form-check form-switch'>
          <input className='form-check-input' type='checkbox' disabled={true} readOnly/>
        </div>
      </td>
      <td>
        <a href='/#' className={classNames(saveClasses)} data-testid='admin-users-add-btn' onClick={onSave}>Lisää
          käyttäjä</a>
      </td>
    </tr>
  )
}

interface AdminUsersRowProps {
  user: AdminUser,
  disabled: boolean
}

export function AdminUsersRow({user, disabled}: Readonly<AdminUsersRowProps>): JSX.Element {
  const dispatch = useAppDispatch()

  function onChangeUseAuthorize(e: ChangeEvent<HTMLInputElement>) {
    dispatch(adminUpdateUserAccess(user.id, {
      email: user.email,
      useAuthorized: e.target.checked,
      isAdmin: user.isAdmin
    }))
  }

  function onChangeIsAdmin(e: ChangeEvent<HTMLInputElement>) {
    dispatch(adminUpdateUserAccess(user.id, {
      email: user.email,
      useAuthorized: user.useAuthorized,
      isAdmin: e.target.checked
    }))
  }

  return (
    <tr>
      <td>{user.email}</td>
      <td>{user.auth0Id}</td>
      <td>
        <div className='form-check form-switch'>
          <input className='form-check-input' type='checkbox'
                 onChange={onChangeUseAuthorize}
                 checked={user.useAuthorized}
                 disabled={disabled}
                 title='salli suunnittelukoneen käyttö'
                 data-testid='admin-users-row-use-authorized-switch'/>
        </div>
      </td>
      <td>
        <div className='form-check form-switch'>
          <input className='form-check-input' type='checkbox'
                 onChange={onChangeIsAdmin}
                 checked={user.isAdmin}
                 disabled={disabled}
                 title='myönnä pääkäyttäjäoikeudet'
                 data-testid='admin-users-row-is-admin-switch'/>
        </div>

      </td>
      <td>&nbsp;
        {disabled && <Tooltip title='oman käyttäjän muutokset estetty'/>}
      </td>
    </tr>
  )
}

/* Helper Functions */

function sortUsers(users: AdminUser[]): AdminUser[] {
  return [...users].sort((u1, u2) => {
    const u1SortStr = `${u1.isAdmin ? '1' : '2'}-${u1.email}`
    const u2SortStr = `${u2.isAdmin ? '1' : '2'}-${u2.email}`
    if (u1SortStr > u2SortStr) {
      return 1
    }

    if (u1SortStr < u2SortStr) {
      return -1
    }

    return 0
  })
}
